import { Fragment, useMemo, useState } from 'react';
import { useAdminListProductRegistrations } from '@/api/auth/admin/admin';
import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ProductPriceList } from './ProductPriceList';
import { resolveEligibleScenarios } from '@/lib/billing-scenarios';
import { formatDate } from '@/lib/format';

interface OrganizationRegistrationsTableProps {
  organization: AdminOrganizationResponse;
}

/**
 * Read-only table of the organization's product registrations. Each row
 * expands to reveal the Stripe price list applicable to this organization for
 * that product, narrowed to the scenarios its pricing tier is eligible for.
 */
export function OrganizationRegistrationsTable({
  organization,
}: OrganizationRegistrationsTableProps) {
  const organizationId = organization.id;

  // Resolve the organization's eligible billing scenarios once from its pricing-tier flags; the
  // expanded price list for every product is filtered to this set.
  const eligibleScenarios = useMemo(
    () =>
      resolveEligibleScenarios(
        organization.isNonProfit,
        organization.isPartner,
        !!organization.sourcePartnerId
      ),
    [organization.isNonProfit, organization.isPartner, organization.sourcePartnerId]
  );

  const { data, isLoading, error } = useAdminListProductRegistrations(
    { organizationId, pageSize: 100 },
    { query: { enabled: !!organizationId } }
  );

  const page = data?.data;
  const items = page?.items ?? [];

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setExpanded((current) => ({ ...current, [id]: !current[id] }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Registrations</CardTitle>
        <CardDescription>
          Products this organization is registered for, with each product's applicable Stripe price
          list
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-destructive py-2">Failed to load product registrations.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>Product</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead>Registered by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(3)].map((_, index) => (
                    <TableRow key={index}>
                      {[...Array(5)].map((__, cell) => (
                        <TableCell key={cell}>
                          <Skeleton className="h-4 w-[100px]" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                      No product registrations found for this organization.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((registration) => (
                    <Fragment key={registration.id}>
                      <TableRow>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={expanded[registration.id] ? 'Collapse' : 'Expand'}
                            onClick={() => toggle(registration.id)}
                          >
                            {expanded[registration.id] ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium font-mono">
                          {registration.productId}
                        </TableCell>
                        <TableCell>{formatDate(registration.createdUtc)}</TableCell>
                        <TableCell>{formatDate(registration.lastSignedInUtc)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {registration.registeredByUserId ?? '—'}
                        </TableCell>
                      </TableRow>
                      {expanded[registration.id] && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30">
                            <div className="p-2">
                              <p className="text-sm font-medium mb-2">
                                Applicable Stripe price list
                              </p>
                              <ProductPriceList
                                productId={registration.productId}
                                eligibleScenarios={eligibleScenarios}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
            {page?.hasMore && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing the first 100 registrations.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
